/**
 * QuoteCallback — compact request-a-callback form.
 * Three fields: name, phone, best time. Fast friction-free conversion.
 *
 * Registered as: quote / quote-callback
 */
import React, { useRef, useState } from "react";
import type { SectionComponentProps } from "../../registry.js";

interface QuoteCallbackProps {
  headline?: string;
  subheadline?: string;
  /** Phone label shown on the button in the success state */
  phoneLabel?: string;
}

type FormState = "idle" | "submitting" | "success" | "error";

export function QuoteCallback({ props }: SectionComponentProps) {
  const {
    headline = "Request a Callback",
    subheadline = "Leave your number and we'll call you back, usually within a few hours.",
    phoneLabel,
  } = props as QuoteCallbackProps;

  const [state, setState] = useState<FormState>("idle");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [time, setTime] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState("submitting");
    try {
      // Wire to your callable / API endpoint here.
      // e.g. await httpsCallable(functions, "requestCallback")({ name, phone, preferredTime: time })
      await new Promise((r) => setTimeout(r, 600));
      setState("success");
    } catch {
      setState("error");
    }
  }

  if (state === "success") {
    return (
      <section className="py-20 px-6 bg-muted/30" id="callback">
        <div className="max-w-md mx-auto text-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: "var(--color-primary)" }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">We'll be in touch!</h2>
          <p className="text-muted-foreground text-sm">
            Thanks {name ? name.split(" ")[0] : ""}. We'll call {phone} {time ? `around ${time}` : "soon"}.
          </p>
          {phoneLabel && (
            <a
              href={`tel:${phoneLabel}`}
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold hover:underline"
              style={{ color: "var(--color-primary)" }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.09 10.78 19.79 19.79 0 01.12 2.2 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.66-.66a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z"/></svg>
              Call us now: {phoneLabel}
            </a>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-6 bg-muted/30" id="callback">
      <div className="max-w-md mx-auto">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">{headline}</h2>
          {subheadline && <p className="text-muted-foreground text-sm">{subheadline}</p>}
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="cb-name" className="block text-sm font-medium text-foreground mb-1.5">
              Your Name
            </label>
            <input
              id="cb-name"
              type="text"
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
              className="w-full rounded-[var(--radius)] border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div>
            <label htmlFor="cb-phone" className="block text-sm font-medium text-foreground mb-1.5">
              Phone Number
            </label>
            <input
              id="cb-phone"
              type="tel"
              required
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 555-5555"
              className="w-full rounded-[var(--radius)] border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div>
            <label htmlFor="cb-time" className="block text-sm font-medium text-foreground mb-1.5">
              Best Time to Call
            </label>
            <select
              id="cb-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full rounded-[var(--radius)] border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">Any time</option>
              <option value="Morning (8am – 12pm)">Morning (8am – 12pm)</option>
              <option value="Afternoon (12pm – 5pm)">Afternoon (12pm – 5pm)</option>
              <option value="Evening (5pm – 8pm)">Evening (5pm – 8pm)</option>
            </select>
          </div>

          {state === "error" && (
            <p className="text-sm text-destructive">Something went wrong. Please try again or call us directly.</p>
          )}

          <button
            type="submit"
            disabled={state === "submitting"}
            className="w-full rounded-[var(--radius)] bg-primary text-primary-foreground py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: "var(--color-primary)" }}
          >
            {state === "submitting" ? "Sending…" : "Request Callback"}
          </button>
        </form>
      </div>
    </section>
  );
}
